import { useState } from 'react'
import { router } from '@inertiajs/react'
import type { FormDataConvertible } from '@inertiajs/core'
import { useTranslation } from 'react-i18next'
import { OrganizationLayout } from '../../layouts/organization-layout'
import { getDateLocale } from '@/lib/i18n'
import { isAdmin as checkIsAdmin } from '@/lib/auth'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '../../components/ui/alert-dialog'
import { Checkbox } from '../../components/ui/checkbox'
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select'
import { UserPlusIcon, MailIcon, XIcon, ClockIcon, TriangleAlertIcon } from 'lucide-react'

interface Organization {
  id: number
  name: string
  description: string | null
  created_at: string
  updated_at: string
}

interface Member {
  id: number
  name: string
  email: string
  organization_role: string
  admin: boolean
  super_admin?: boolean
  avatar_url?: string
  created_at: string
}

interface PendingInvitation {
  id: number
  email: string
  role: string
  project_assignments: { project_id: number }[]
  invited_by: string
  expires_at: string
  created_at: string
}

interface Project {
  id: number
  name: string
}

interface OrganizationMembersProps {
  organization: Organization
  members: Member[]
  pending_invitations: PendingInvitation[]
  projects: Project[]
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
      organization_role: string
    }
  }
}

function Members({ organization, members, pending_invitations, projects = [], auth }: OrganizationMembersProps) {
  const { t } = useTranslation('organizations')
  const [isInviteOpen, setIsInviteOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<string>('member')
  const [accessAllProjects, setAccessAllProjects] = useState(false)
  const [projectAssignments, setProjectAssignments] = useState<{ project_id: number }[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [removingMemberId, setRemovingMemberId] = useState<number | null>(null)
  const [cancelingInvitationId, setCancelingInvitationId] = useState<number | null>(null)
  const [changingRoleMemberId, setChangingRoleMemberId] = useState<number | null>(null)
  const isAdmin = checkIsAdmin(auth.user)

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    const data: Record<string, FormDataConvertible> = { email, role }
    if (role === 'external') {
      data.access_all_projects = accessAllProjects
      if (!accessAllProjects && projectAssignments.length > 0) {
        data.project_assignments = projectAssignments
      }
    }

    router.post('/organization_invitations', data, {
      onSuccess: () => {
        setEmail('')
        setRole('member')
        setAccessAllProjects(false)
        setProjectAssignments([])
        setIsInviteOpen(false)
      },
      onFinish: () => {
        setIsSubmitting(false)
      }
    })
  }

  const toggleProject = (projectId: number) => {
    setProjectAssignments(prev => {
      const exists = prev.find(p => p.project_id === projectId)
      if (exists) {
        return prev.filter(p => p.project_id !== projectId)
      }
      return [...prev, { project_id: projectId }]
    })
  }

  const handleCancelInvitation = (invitationId: number) => {
    router.delete(`/organization_invitations/${invitationId}`, {
      onFinish: () => {
        setCancelingInvitationId(null)
      }
    })
  }

  const handleResendInvitation = (invitationId: number) => {
    router.post(`/organization_invitations/${invitationId}/resend`)
  }

  const handleRemoveMember = (memberId: number) => {
    setRemovingMemberId(memberId)
    router.post(`/organization/remove_member/${memberId}`, {}, {
      onFinish: () => {
        setRemovingMemberId(null)
      }
    })
  }

  const handleChangeRole = (memberId: number, newRole: string) => {
    setChangingRoleMemberId(memberId)
    router.post(`/organization/change_member_role/${memberId}`, { role: newRole }, {
      onFinish: () => {
        setChangingRoleMemberId(null)
      }
    })
  }

  return (
    <>
      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
            <div className="px-4 lg:px-6">

              {/* Page Header */}
              <div className="mb-8 flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-foreground">{t('members.pageTitle')}</h1>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {t('members.pageDescription')}
                  </p>
                </div>

                {isAdmin && (
                  <Dialog open={isInviteOpen} onOpenChange={(open) => {
                    setIsInviteOpen(open)
                    if (!open) {
                      setEmail('')
                      setRole('member')
                      setAccessAllProjects(false)
                      setProjectAssignments([])
                    }
                  }}>
                    <DialogTrigger asChild>
                      <Button>
                        <UserPlusIcon className="mr-2 h-4 w-4" />
                        {t('members.invite')}
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <form onSubmit={handleInvite}>
                        <DialogHeader>
                          <DialogTitle>{t('members.dialogTitle')}</DialogTitle>
                          <DialogDescription>
                            {t('members.dialogDescription', { organizationName: organization.name })}
                          </DialogDescription>
                        </DialogHeader>
                        <div className="grid gap-4 py-4">
                          <div className="grid gap-2">
                            <Label htmlFor="email">{t('members.email')}</Label>
                            <Input
                              id="email"
                              type="email"
                              placeholder={t('members.emailPlaceholder')}
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              required
                            />
                          </div>
                          <div className="grid gap-2">
                            <Label>{t('members.role')}</Label>
                            <RadioGroup value={role} onValueChange={(val) => {
                              setRole(val)
                              if (val !== 'external') {
                                setAccessAllProjects(false)
                                setProjectAssignments([])
                              }
                            }} className="grid gap-2">
                              <label
                                htmlFor="role-admin"
                                className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                  role === 'admin' ? 'border-primary bg-accent/50' : 'border-border hover:bg-accent/50'
                                }`}
                              >
                                <RadioGroupItem value="admin" id="role-admin" className="mt-0.5" />
                                <div className="flex-1">
                                  <div className="font-medium text-sm">{t('members.roleAdmin')}</div>
                                  <div className="text-xs text-muted-foreground mt-0.5">{t('members.roleAdminDescription')}</div>
                                </div>
                              </label>
                              <label
                                htmlFor="role-member"
                                className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                  role === 'member' ? 'border-primary bg-accent/50' : 'border-border hover:bg-accent/50'
                                }`}
                              >
                                <RadioGroupItem value="member" id="role-member" className="mt-0.5" />
                                <div className="flex-1">
                                  <div className="font-medium text-sm">{t('members.roleMember')}</div>
                                  <div className="text-xs text-muted-foreground mt-0.5">{t('members.roleMemberDescription')}</div>
                                </div>
                              </label>
                              <label
                                htmlFor="role-external"
                                className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                  role === 'external' ? 'border-primary bg-accent/50' : 'border-border hover:bg-accent/50'
                                }`}
                              >
                                <RadioGroupItem value="external" id="role-external" className="mt-0.5" />
                                <div className="flex-1">
                                  <div className="font-medium text-sm">{t('members.roleExternal')}</div>
                                  <div className="text-xs text-muted-foreground mt-0.5">{t('members.roleExternalDescription')}</div>
                                </div>
                              </label>
                            </RadioGroup>
                          </div>
                          {role === 'external' && (
                            <div className="grid gap-2">
                              <Label>{t('members.projectAccess')}</Label>
                              <RadioGroup value={accessAllProjects ? 'all' : 'specific'} onValueChange={(val) => {
                                setAccessAllProjects(val === 'all')
                                if (val === 'all') setProjectAssignments([])
                              }} className="grid gap-2">
                                <label
                                  htmlFor="access-all"
                                  className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                    accessAllProjects ? 'border-primary bg-accent/50' : 'border-border hover:bg-accent/50'
                                  }`}
                                >
                                  <RadioGroupItem value="all" id="access-all" className="mt-0.5" />
                                  <div className="flex-1">
                                    <div className="font-medium text-sm">{t('members.accessAllProjects')}</div>
                                    <div className="text-xs text-muted-foreground mt-0.5">{t('members.accessAllProjectsDescription')}</div>
                                  </div>
                                </label>
                                <label
                                  htmlFor="access-specific"
                                  className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                    !accessAllProjects ? 'border-primary bg-accent/50' : 'border-border hover:bg-accent/50'
                                  }`}
                                >
                                  <RadioGroupItem value="specific" id="access-specific" className="mt-0.5" />
                                  <div className="flex-1">
                                    <div className="font-medium text-sm">{t('members.accessSpecificProjects')}</div>
                                    <div className="text-xs text-muted-foreground mt-0.5">{t('members.accessSpecificProjectsDescription')}</div>
                                  </div>
                                </label>
                              </RadioGroup>
                              {!accessAllProjects && (
                                <>
                                  {projects.length === 0 ? (
                                    <p className="text-sm text-muted-foreground italic py-2">{t('members.noProjects')}</p>
                                  ) : (
                                    <div className="space-y-2 max-h-48 overflow-y-auto">
                                      {projects.map((project) => {
                                        const isChecked = projectAssignments.some(p => p.project_id === project.id)

                                        return (
                                          <label
                                            key={project.id}
                                            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                              isChecked ? 'border-primary bg-accent/50' : 'border-border hover:bg-accent/50'
                                            }`}
                                          >
                                            <Checkbox
                                              checked={isChecked}
                                              onCheckedChange={() => toggleProject(project.id)}
                                              className="flex-shrink-0"
                                            />
                                            <span className="flex-1 text-sm font-medium truncate">{project.name}</span>
                                          </label>
                                        )
                                      })}
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                        <DialogFooter>
                          <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? t('members.sendingButton') : t('members.sendButton')}
                          </Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}
              </div>

              {/* Members Section */}
              <div className="space-y-6">
                {/* Current Members */}
                <Card className="w-full shadow-none">
                  <CardHeader>
                    <CardTitle>{t('members.cardTitle', { count: members.length })}</CardTitle>
                    <CardDescription>
                      {t('members.cardDescription')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {members.map((member) => {
                        const initials = member.name.split(' ').map(n => n[0]).join('').toUpperCase()

                        return (
                        <div key={member.id} className="flex flex-wrap items-center justify-between gap-3 p-4 bg-muted rounded-lg border border-border">
                          <div className="flex items-center gap-4 min-w-0 flex-1">
                            <Avatar className="h-10 w-10 flex-shrink-0">
                              <AvatarImage src={member.avatar_url || undefined} alt={member.name} />
                              <AvatarFallback className="bg-primary/10 text-primary font-semibold text-lg border border-primary/20">
                                {initials}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0 flex-1">
                              <p className="text-base font-medium text-foreground truncate">{member.name}</p>
                              <p className="text-sm text-muted-foreground truncate">{member.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {isAdmin && member.id !== auth.user.id ? (
                              <Select
                                value={member.organization_role}
                                onValueChange={(newRole) => handleChangeRole(member.id, newRole)}
                                disabled={changingRoleMemberId === member.id}
                              >
                                <SelectTrigger className="w-[120px] h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="admin">{t('members.roleAdmin')}</SelectItem>
                                  <SelectItem value="member">{t('members.roleMember')}</SelectItem>
                                  <SelectItem value="external">{t('members.roleExternal')}</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <Badge
                                variant={member.organization_role === 'external' ? 'outline' : 'secondary'}
                                className="whitespace-nowrap"
                              >
                                {member.organization_role === 'admin'
                                  ? t('members.roleAdmin')
                                  : member.organization_role === 'external'
                                    ? t('members.roleExternal')
                                    : t('members.roleMember')}
                              </Badge>
                            )}
                            {isAdmin && member.id !== auth.user.id && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                  >
                                    <XIcon className="h-4 w-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader className="items-center">
                                    <div className="bg-orange-100 mx-auto mb-2 flex size-12 items-center justify-center rounded-full">
                                      <TriangleAlertIcon className="text-orange-600 size-6" />
                                    </div>
                                    <AlertDialogTitle>{t('members.removeDialogTitle')}</AlertDialogTitle>
                                    <AlertDialogDescription className="text-center">
                                      {t('members.removeDialogDescription', { memberName: member.name, organizationName: organization.name })}
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>{t('actions.cancel')}</AlertDialogCancel>
                                    <AlertDialogAction
                                      className="bg-orange-600 hover:bg-orange-700 focus-visible:ring-orange-600 text-white"
                                      onClick={() => handleRemoveMember(member.id)}
                                      disabled={removingMemberId === member.id}
                                    >
                                      {removingMemberId === member.id ? t('members.removingButton') : t('members.removeButton')}
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </div>
                        </div>
                        )
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Pending Invitations */}
                {isAdmin && pending_invitations.length > 0 && (
                  <Card className="w-full shadow-none">
                    <CardHeader>
                      <CardTitle>{t('members.pendingTitle', { count: pending_invitations.length })}</CardTitle>
                      <CardDescription>
                        {t('members.pendingDescription')}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {pending_invitations.map((invitation) => (
                          <div key={invitation.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-warning/10 rounded-lg border border-warning/30">
                            <div className="flex items-center gap-4 min-w-0 flex-1">
                              <div className="w-10 h-10 flex-shrink-0 bg-amber-600 text-white rounded-full flex items-center justify-center">
                                <MailIcon className="h-5 w-5" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-base font-medium text-foreground truncate">{invitation.email}</p>
                                <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                                  <div className="flex items-center gap-1">
                                    <ClockIcon className="h-3 w-3 flex-shrink-0" />
                                    <span>{t('members.invitedBy', { name: invitation.invited_by })}</span>
                                  </div>
                                  <span>•</span>
                                  <span>{t('members.expires', { date: new Date(invitation.expires_at).toLocaleDateString(getDateLocale()) })}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <Badge variant="warning">
                                {t('members.pending')}
                              </Badge>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleResendInvitation(invitation.id)}
                              >
                                {t('members.resendInvite')}
                              </Button>
                              <AlertDialog open={cancelingInvitationId === invitation.id} onOpenChange={(open) => {
                                if (!open) setCancelingInvitationId(null)
                              }}>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setCancelingInvitationId(invitation.id)}
                                  >
                                    <XIcon className="h-4 w-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader className="items-center">
                                    <div className="bg-orange-100 mx-auto mb-2 flex size-12 items-center justify-center rounded-full">
                                      <TriangleAlertIcon className="text-orange-600 size-6" />
                                    </div>
                                    <AlertDialogTitle>{t('members.cancelDialogTitle')}</AlertDialogTitle>
                                    <AlertDialogDescription className="text-center">
                                      {t('members.cancelDialogDescription', { email: invitation.email })}
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>{t('actions.cancel')}</AlertDialogCancel>
                                    <AlertDialogAction
                                      className="bg-orange-600 hover:bg-orange-700 focus-visible:ring-orange-600 text-white"
                                      onClick={() => handleCancelInvitation(invitation.id)}
                                    >
                                      {t('members.cancelButton')}
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  )
}

Members.layout = (page: React.ReactNode) => <OrganizationLayout>{page}</OrganizationLayout>

export default Members
